// Shared helpers for computing the upcoming trial-eligible dates of a class,
// keyed off its `day_of_week` and `term_dates` string. Used by BOTH the
// admin Trial-Availability UI (to render checkboxes) and the public
// FreeTrial.jsx (to render the parent's date picker).

const DAY_MAP = {
  sunday: 0, sun: 0, sundays: 0,
  monday: 1, mon: 1, mondays: 1,
  tuesday: 2, tue: 2, tuesdays: 2,
  wednesday: 3, wed: 3, wednesdays: 3,
  thursday: 4, thu: 4, thursdays: 4,
  friday: 5, fri: 5, fridays: 5,
  saturday: 6, sat: 6, saturdays: 6,
};

const MONTHS = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
  sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10,
  dec: 11, december: 11,
};

// Parse a term_dates string like "Jan 6 - Mar 15, 2026" into { start, end } Date objects.
// Falls back to today + 60 days when the string is missing or unparseable.
export const parseTermDates = (termDates) => {
  const fallbackStart = new Date();
  const fallbackEnd = new Date();
  fallbackEnd.setDate(fallbackEnd.getDate() + 60);
  if (!termDates) return { start: fallbackStart, end: fallbackEnd };

  try {
    const cleaned = termDates.trim().replace(/\s+/g, ' ');
    // Extract year (defaults to current year).
    const yearMatch = cleaned.match(/(\d{4})/);
    const year = yearMatch ? parseInt(yearMatch[1], 10) : new Date().getFullYear();

    // Split into "start" and "end" halves using a dash / en-dash / "to".
    const parts = cleaned.split(/\s*(?:-|–|—|to)\s*/i).map((p) => p.replace(/,\s*\d{4}.*$/, '').trim());
    if (parts.length < 2) return { start: fallbackStart, end: fallbackEnd };

    const parsePart = (str, defYear = year) => {
      // "Jan 6" or "6 Jan"
      const m = str.match(/([A-Za-z]+)\s+(\d{1,2})/) || str.match(/(\d{1,2})\s+([A-Za-z]+)/);
      if (!m) return null;
      const monthTok = (m[1].match(/[A-Za-z]+/) ? m[1] : m[2]).toLowerCase();
      const dayNum = parseInt(m[1].match(/\d+/) ? m[1] : m[2], 10);
      const month = MONTHS[monthTok];
      if (month == null || Number.isNaN(dayNum)) return null;
      return new Date(defYear, month, dayNum);
    };
    const start = parsePart(parts[0]);
    const end = parsePart(parts[1]);
    if (!start || !end) return { start: fallbackStart, end: fallbackEnd };
    // If the end is before the start, roll the year forward — e.g. "Nov 1 - Jan 20" spans New Year.
    if (end < start) end.setFullYear(end.getFullYear() + 1);
    return { start, end };
  } catch {
    return { start: fallbackStart, end: fallbackEnd };
  }
};

// Return the target weekday index (0-6) for a class based on its
// day_of_week string. `Monday & Wednesday` picks the first token; if
// unparseable, returns null.
export const classTargetDay = (cls) => {
  if (!cls?.day_of_week) return null;
  let dayStr = cls.day_of_week.toLowerCase().split(/[&,/\s]+/)[0].trim();
  if (DAY_MAP[dayStr] !== undefined) return DAY_MAP[dayStr];
  if (dayStr.endsWith('s') && DAY_MAP[dayStr.slice(0, -1)] !== undefined) {
    return DAY_MAP[dayStr.slice(0, -1)];
  }
  return null;
};

// Return an array of ISO date strings (YYYY-MM-DD) for every occurrence of
// this class within its term, starting from today.
export const upcomingClassDates = (cls, { maxCount = 20 } = {}) => {
  const targetDay = classTargetDay(cls);
  if (targetDay == null) return [];
  const { start, end } = parseTermDates(cls.term_dates);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const from = start > today ? start : today;
  const dates = [];
  const cursor = new Date(from);
  // Advance to the next matching weekday.
  while (cursor.getDay() !== targetDay) {
    cursor.setDate(cursor.getDate() + 1);
    if (cursor > end) return dates;
  }
  while (cursor <= end && dates.length < maxCount) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 7);
  }
  return dates;
};

export const formatTrialDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};
